import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { BreadcrumbSchema, HowToSchema, OrganizationSchema, SoftwareApplicationSchema } from '@/components/SEOSchema';
import {
  Calculator, Check, ChevronDown, Shield, Zap, Euro, Users, TrendingUp, PiggyBank,
  AlertTriangle, X, FileText, Clock, CreditCard, Target, Video, QrCode, Share2,
} from 'lucide-react';
import { useCalEmbed, openCalModal } from '@/hooks/useCalEmbed';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import Footer from '@/components/Footer';
import SeoClusterLinks from '@/components/SeoClusterLinks';
import { useTracking, useScrollTracking, useSectionTracking } from '@/hooks/useTracking';
import { useHreflang, HREFLANG_SETS } from '@/hooks/useHreflang';
import { formatCurrency } from '@/utils/format';
import { SWISS_CANTONS } from '../config/steuerruecklagen';
import { SeoHero } from '../components/SeoHero';
import { SeoSection } from '../components/SeoSection';
import { TableOfContents } from '../components/TableOfContents';
import { StickyMobileCTA } from '../components/StickyMobileCTA';
import { TrustStrip } from '../components/TrustStrip';
import { Callout } from '../components/Callout';
import { useShareableCalc } from '../hooks/useShareableCalc';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const ICON_MAP = { Calculator, Check, Shield, Zap, Euro, Users, TrendingUp, PiggyBank, AlertTriangle, FileText, Clock, CreditCard, Target, QrCode };
const RATE_BG = { emerald: "bg-emerald-100 text-emerald-700", blue: "bg-sky-100 text-sky-700", orange: "bg-amber-100 text-amber-700", purple: "bg-violet-100 text-violet-700" };

function TrustBadge({ icon, label, light }) {
  const Icon = ICON_MAP[icon] || Check;
  return <div className="flex items-center gap-2"><Icon className={`h-4 w-4 ${light ? 'text-emerald-300' : 'text-emerald-600'}`} /><span>{label}</span></div>;
}

function CalculatorCard({ cfg, taxYear, state, setState, onCalculate, isCalculating, result, onShare, fmt, isShared }) {
  const { revenue, expenses, canton, calcOptions } = state;
  const pct = result && result.taxable_income > 0 ? Math.round((result.total_tax / result.taxable_income) * 100) : null;
  const monthly = result ? result.total_tax / 12 : 0;
  const barWidth = pct ? Math.min(100, Math.max(6, pct)) : 0;
  return (
    <div data-testid="calculator-card">
      {isShared && <p className="mb-3 rounded-lg bg-emerald-50 px-3 py-2 text-xs text-emerald-800" data-testid="shared-result-banner">Jemand hat diese Berechnung mit dir geteilt – Werte sind vorbefüllt.</p>}
      <div className="grid gap-4 sm:grid-cols-2 sm:items-end">
        <div>
          <Label htmlFor="revenue" className="block min-h-[2.5rem] sm:flex sm:items-end text-sm font-semibold text-gray-800 leading-tight">{cfg.calcRevenueLabel}</Label>
          <Input id="revenue" type="number" inputMode="decimal" value={revenue} onChange={(e) => setState(s => ({ ...s, revenue: e.target.value }))} placeholder="z. B. 80000" className="mt-1.5 h-11 text-base" data-testid="calc-revenue-input" />
        </div>
        <div>
          <Label htmlFor="expenses" className="block min-h-[2.5rem] sm:flex sm:items-end text-sm font-semibold text-gray-800 leading-tight">{cfg.calcExpensesLabel}</Label>
          <Input id="expenses" type="number" inputMode="decimal" value={expenses} onChange={(e) => setState(s => ({ ...s, expenses: e.target.value }))} placeholder="z. B. 15000" className="mt-1.5 h-11 text-base" data-testid="calc-expenses-input" />
        </div>
      </div>
      {cfg.calcHasCantonSelect && (
        <div className="mt-4">
          <Label htmlFor="canton" className="text-sm font-semibold text-gray-800">Kanton</Label>
          <select id="canton" value={canton} onChange={(e) => setState(s => ({ ...s, canton: e.target.value }))} className="mt-1.5 h-11 w-full rounded-md border border-gray-300 bg-white px-3 text-base" data-testid="calc-canton-select">
            {SWISS_CANTONS.map(c => <option key={c.code} value={c.code}>{c.name}</option>)}
          </select>
        </div>
      )}
      <div className="mt-4 space-y-2">
        {(cfg.calcOptions || []).map(opt => (
          <label key={opt.id} htmlFor={opt.id} className="flex items-center gap-2 text-sm text-gray-700">
            <input type="checkbox" id={opt.id} checked={calcOptions[opt.field] || false} onChange={(e) => setState(s => ({ ...s, calcOptions: { ...s.calcOptions, [opt.field]: e.target.checked } }))} className="rounded border-gray-300 text-emerald-600" />
            {opt.label}
          </label>
        ))}
      </div>
      <Button onClick={onCalculate} disabled={isCalculating} className="mt-5 w-full bg-emerald-600 hover:bg-emerald-700 text-white h-12 text-base font-semibold" data-testid="calculate-button">
        <Calculator className="h-5 w-5 mr-2" /> {isCalculating ? 'Berechne…' : 'Rücklage berechnen'}
      </Button>

      {result && (
        <div className="mt-5 rounded-xl bg-[#062417] seo-grain p-5 text-white" data-testid="calc-result">
          <div className="flex items-end justify-between gap-4">
            <div>
              <p className="text-xs uppercase tracking-wider text-emerald-200/80">Rücklage pro Monat</p>
              <p className="font-display text-3xl font-bold" data-testid="calc-monthly">{fmt(monthly)}</p>
            </div>
            <div className="text-right">
              <p className="text-xs uppercase tracking-wider text-emerald-200/80">Anteil vom Gewinn</p>
              <p className="font-display text-2xl font-bold" data-testid="calc-percent">{pct !== null ? `${pct} %` : '–'}</p>
            </div>
          </div>
          <div className="mt-3 h-2.5 w-full rounded-full bg-white/10" aria-hidden="true">
            <div className="h-2.5 rounded-full bg-emerald-400 transition-[width] duration-500" style={{ width: `${barWidth}%` }} />
          </div>
          <dl className="mt-4 grid grid-cols-2 gap-x-4 gap-y-1.5 text-sm">
            {cfg.calcResultFields.map(f => {
              if (f.showIf && !calcOptions[f.showIf]) return null;
              return <React.Fragment key={f.key}><dt className="text-emerald-100/80">{f.label}</dt><dd className="text-right font-medium">{fmt(result[f.key])}</dd></React.Fragment>;
            })}
            {result.canton_name && <><dt className="text-emerald-100/80">Kanton</dt><dd className="text-right font-medium">{result.canton_name}</dd></>}
            <dt className="border-t border-white/10 pt-2 font-semibold">Gesamt pro Jahr</dt><dd className="border-t border-white/10 pt-2 text-right font-semibold" data-testid="calc-total">{fmt(result.total_tax)}</dd>
            <dt className="text-emerald-200">Netto bleibt</dt><dd className="text-right font-semibold text-emerald-300" data-testid="calc-net">{fmt(result.net_profit)}</dd>
          </dl>
          <div className="mt-4 flex flex-col sm:flex-row gap-2">
            <Button type="button" onClick={onShare} variant="outline" className="border-white/30 bg-transparent text-white hover:bg-white/10 hover:text-white" data-testid="share-result-button"><Share2 className="h-4 w-4 mr-2" /> Ergebnis teilen</Button>
            <Link to="/signup" className="flex-1"><Button className="w-full bg-white text-[#062417] hover:bg-emerald-50 font-semibold" data-testid="calc-cta-button">Live aus echten Daten rechnen</Button></Link>
          </div>
          <p className="mt-3 text-[11px] leading-relaxed text-emerald-100/70">Vereinfachte Schätzung für {taxYear}. Nicht enthalten: {cfg.calcDisclaimerItems.join(', ')}{cfg.calcDisclaimerConditional?.showIf && calcOptions[cfg.calcDisclaimerConditional.showIf] ? `, ${cfg.calcDisclaimerConditional.text}` : ''}. Ersetzt keine Steuerberatung.</p>
        </div>
      )}
    </div>
  );
}

export default function SteuerruecklagenTemplate({ cfg }) {
  const taxYear = new Date().getFullYear();
  const [openFaq, setOpenFaq] = useState(null);
  const { getNumber, getString, isShared, share } = useShareableCalc({ path: cfg.path, title: cfg.title(taxYear), markerKey: 'einkommen' });
  const [state, setState] = useState(() => {
    const calcOptions = {};
    (cfg.calcOptions || []).forEach(o => { calcOptions[o.field] = o.defaultChecked || false; });
    const rev = getNumber('einkommen', null);
    const exp = getNumber('ausgaben', null);
    return { revenue: rev === null ? '' : String(rev), expenses: exp === null ? '' : String(exp), canton: getString('kanton', 'ZH', SWISS_CANTONS.map(c => c.code)), calcOptions };
  });
  const [result, setResult] = useState(null);
  const [isCalculating, setIsCalculating] = useState(false);

  useCalEmbed();
  const { trackCTAClick, trackFAQOpen } = useTracking();
  useScrollTracking();

  const heroRef = useRef(null);
  const whyRef = useRef(null);
  const howMuchRef = useRef(null);
  const ivoliqRef = useRef(null);
  const vorauszahlungRef = useRef(null);
  const mistakesRef = useRef(null);
  const faqRef = useRef(null);
  const finalCtaRef = useRef(null);
  useSectionTracking('hero', heroRef);
  useSectionTracking('why_important', whyRef);
  useSectionTracking('how_much', howMuchRef);
  useSectionTracking('ivoliq', ivoliqRef);
  useSectionTracking('vorauszahlung', vorauszahlungRef);
  useSectionTracking('mistakes', mistakesRef);
  useSectionTracking('faq', faqRef);
  useSectionTracking('final_cta', finalCtaRef);

  useHreflang({ currentUrl: HREFLANG_SETS.steuerruecklagen[cfg.hreflangKey], alternates: HREFLANG_SETS.steuerruecklagen, xDefault: HREFLANG_SETS.steuerruecklagen.xDefault });

  useEffect(() => {
    document.title = cfg.title(taxYear);
    const metaDesc = document.querySelector('meta[name="description"]');
    if (metaDesc) metaDesc.setAttribute('content', cfg.metaDescription(taxYear));
    const ogTags = {
      'og:title': cfg.title(taxYear), 'og:description': cfg.metaDescription(taxYear), 'og:type': 'website', 'og:url': cfg.ogUrl,
      'og:site_name': 'ivoliq', 'og:locale': cfg.ogLocale, 'twitter:card': 'summary_large_image', 'twitter:title': cfg.title(taxYear), 'twitter:description': cfg.metaDescription(taxYear),
    };
    Object.entries(ogTags).forEach(([prop, content]) => {
      const isOg = prop.startsWith('og:');
      const sel = isOg ? `meta[property="${prop}"]` : `meta[name="${prop}"]`;
      let tag = document.querySelector(sel);
      if (!tag) { tag = document.createElement('meta'); if (isOg) tag.setAttribute('property', prop); else tag.setAttribute('name', prop); document.head.appendChild(tag); }
      tag.setAttribute('content', content);
    });
    let canonical = document.querySelector('link[rel="canonical"]');
    if (!canonical) { canonical = document.createElement('link'); canonical.rel = 'canonical'; document.head.appendChild(canonical); }
    canonical.href = cfg.canonicalUrl;
  }, [cfg, taxYear]);

  const calculateTax = useCallback(async (s) => {
    setIsCalculating(true);
    try {
      const body = {
        country: cfg.country, revenue: parseFloat(s.revenue) || 0, expenses: parseFloat(s.expenses) || 0,
        is_gewerblich: s.calcOptions.is_gewerblich || false, include_social_security: s.calcOptions.include_social_security !== false,
        canton: s.canton, year: taxYear,
      };
      const res = await fetch(`${API}/public/tax-estimate`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) });
      if (res.ok) setResult(await res.json());
    } catch (e) {
      console.error('Tax estimate error:', e);
    } finally {
      setIsCalculating(false);
    }
  }, [cfg.country, taxYear]);

  const autoCalcDone = useRef(false);
  useEffect(() => {
    if (isShared && !autoCalcDone.current) { autoCalcDone.current = true; calculateTax(state); }
  }, [isShared, calculateTax, state]);

  const fmt = (amount) => formatCurrency(amount, cfg.country);
  const onShare = () => {
    const values = { einkommen: state.revenue, ausgaben: state.expenses, land: cfg.country.toLowerCase() };
    if (cfg.calcHasCantonSelect) values.kanton = state.canton;
    share(values, `Meine Steuerrücklage als Freelancer (${cfg.breadcrumbLast}): ${result ? fmt(result.total_tax / 12) : ''} pro Monat – berechne deine:`);
    trackCTAClick('calculator', 'Ergebnis teilen');
  };

  const toggleFaq = (index, question) => { setOpenFaq(openFaq === index ? null : index); if (openFaq !== index) trackFAQOpen(question); };
  const faqSchema = { "@context": "https://schema.org", "@type": "FAQPage", "mainEntity": cfg.faqs.map(faq => ({ "@type": "Question", "name": faq.question, "acceptedAnswer": { "@type": "Answer", "text": faq.answer } })) };

  const extra = cfg.extraSections || [];
  const toc = [
    { id: 'warum', label: 'Warum Rücklagen' },
    { id: 'wieviel', label: 'Wie viel %' },
    ...extra.map(s => ({ id: s.id, label: s.tocLabel })),
    { id: 'vorauszahlung', label: 'Fristen' },
    { id: 'fehler', label: 'Fehler vermeiden' },
    { id: 'faq', label: 'FAQ' },
  ];

  return (
    <div className="min-h-screen bg-white">
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema) }} />
      <BreadcrumbSchema items={[{ name: "Home", url: "/" }, { name: "Steuerrücklagen", url: "/steuerruecklagen-freelancer" }, { name: cfg.breadcrumbLast }]} />
      <SoftwareApplicationSchema country={cfg.country} />
      <HowToSchema name={cfg.howToName} description={cfg.howToDescription} steps={cfg.howToSteps} />
      <OrganizationSchema />

      <SeoHero
        heroRef={heroRef}
        eyebrow={cfg.heroEyebrow(taxYear)}
        title={cfg.heroTitle}
        subtitle={cfg.heroSubtitle(taxYear)}
        bullets={cfg.heroBullets}
        actions={<>
          <a href="#calculator-card" onClick={() => trackCTAClick('hero', 'Jetzt berechnen')} data-testid="hero-calc-button" className="lg:hidden"><Button size="lg" className="w-full bg-emerald-600 hover:bg-emerald-700 text-white h-12 px-8 text-base">Jetzt berechnen</Button></a>
          <Link to="/signup" onClick={() => trackCTAClick('hero', 'Kostenlos starten')} data-testid="hero-cta-button"><Button size="lg" variant="outline" className="w-full h-12 px-8 text-base">Kostenlos starten</Button></Link>
        </>}
        trust={cfg.trustBadges.map((b, i) => <TrustBadge key={i} {...b} />)}
        mediaLabel={cfg.calcTitle(taxYear)}
        media={<div id="calculator-card"><CalculatorCard cfg={cfg} taxYear={taxYear} state={state} setState={setState} onCalculate={() => calculateTax(state)} isCalculating={isCalculating} result={result} onShare={onShare} fmt={fmt} isShared={isShared} /></div>}
      />

      <TableOfContents items={toc} />

      <SeoSection id="warum" variant="soft" title={cfg.whyImportantTitle} sectionRef={whyRef} testId="why-important-section" width="max-w-3xl">
        <div className="space-y-5 text-base md:text-lg leading-relaxed text-gray-700">{cfg.whyImportantParagraphs.map((p, i) => <p key={i}>{p}</p>)}</div>
      </SeoSection>

      <SeoSection id="wieviel" title={cfg.howMuchTitle} sectionRef={howMuchRef} testId="how-much-section">
        <div className="grid gap-5 sm:grid-cols-2">
          {cfg.taxRates.map((r, i) => (
            <div key={i} className="rounded-2xl bg-white p-6 ring-1 ring-gray-200 transition-shadow hover:shadow-lg">
              <div className="flex items-center gap-4">
                <div className={`flex h-11 w-11 items-center justify-center rounded-xl ${RATE_BG[r.color]}`}><Calculator className="h-5 w-5" /></div>
                <div><h3 className="font-semibold text-gray-900">{r.label}</h3><p className="font-display text-2xl font-bold text-gray-900">{r.rate}</p></div>
              </div>
              <p className="mt-3 text-sm text-gray-600">{r.description}</p>
            </div>
          ))}
        </div>
        <div className="mt-8"><Callout variant="tip" title="Faustregel">{cfg.taxSummary}</Callout></div>
      </SeoSection>

      {extra.map((s, idx) => (
        <SeoSection key={s.id} id={s.id} variant={idx % 2 === 0 ? 'soft' : 'plain'} title={s.title} width="max-w-3xl" testId={`extra-${s.id}-section`}>
          <div className="space-y-5 text-base md:text-lg leading-relaxed text-gray-700">{s.paragraphs.map((p, i) => <p key={i}>{p}</p>)}</div>
          {s.callout && <div className="mt-6"><Callout variant={s.callout.variant} title={s.callout.title}>{s.callout.text}</Callout></div>}
        </SeoSection>
      ))}

      <SeoSection id="vorauszahlung" variant={extra.length % 2 === 0 ? 'soft' : 'plain'} title={cfg.vorauszahlungTitle} sectionRef={vorauszahlungRef} testId="vorauszahlung-section">
        <div className="space-y-5 text-base md:text-lg leading-relaxed text-gray-700 max-w-3xl">{cfg.vorauszahlungIntro.map((p, i) => <p key={i}>{p}</p>)}</div>
        <ol className="mt-10 grid gap-4 grid-cols-2 lg:grid-cols-4">
          {cfg.vorauszahlungQuarters.map((q, i) => (
            <li key={i} className="rounded-2xl bg-white p-5 ring-1 ring-gray-200">
              <span className="inline-flex h-9 w-9 items-center justify-center rounded-full bg-[#062417] text-sm font-bold text-white">{q.label}</span>
              <p className="mt-3 font-semibold text-gray-900">{q.date}</p>
              <p className="text-sm text-gray-500">{q.note || 'Vorauszahlung'}</p>
            </li>
          ))}
        </ol>
      </SeoSection>

      <SeoSection id="ivoliq" variant="accent" title={cfg.ivoliqTitle} subtitle={cfg.ivoliqSubtitle} sectionRef={ivoliqRef} testId="ivoliq-section">
        <ol className="grid gap-6 md:grid-cols-3">
          {cfg.ivoliqSteps.map((step, i) => (
            <li key={i} className="rounded-2xl bg-white/5 p-6 ring-1 ring-white/10 backdrop-blur">
              <span className="font-display text-4xl font-bold text-emerald-400">{i + 1}</span>
              <h3 className="mt-3 text-lg font-semibold">{step.title}</h3>
              <p className="mt-2 text-sm text-emerald-100/80">{step.description}</p>
            </li>
          ))}
        </ol>
        <ul className="mt-10 grid gap-3 sm:grid-cols-2">
          {cfg.ivoliqFeatures.map((f, i) => <li key={i} className="flex items-start gap-3 text-emerald-50"><Check className="mt-0.5 h-5 w-5 flex-none text-emerald-400" /><span>{f}</span></li>)}
        </ul>
        <div className="mt-10 flex flex-col sm:flex-row sm:items-center gap-5">
          <Link to="/signup" onClick={() => trackCTAClick('ivoliq', 'Jetzt kostenlos testen')}><Button size="lg" className="bg-white text-[#062417] hover:bg-emerald-50 h-12 px-8 text-base font-semibold">Jetzt kostenlos testen</Button></Link>
          <TrustStrip dark />
        </div>
      </SeoSection>

      <SeoSection id="fehler" title={cfg.mistakesTitle} sectionRef={mistakesRef} testId="mistakes-section">
        <div className="grid gap-x-8 gap-y-3 md:grid-cols-2">
          <h3 className="flex items-center gap-2 text-lg font-semibold text-red-700"><X className="h-5 w-5" /> Das solltest du nicht tun</h3>
          <h3 className="hidden md:flex items-center gap-2 text-lg font-semibold text-emerald-700"><Check className="h-5 w-5" /> So machst du es richtig</h3>
          {Array.from({ length: Math.max(cfg.donts.length, cfg.dos.length) }).map((_, i) => (
            <React.Fragment key={i}>
              <div className="contents md:block">
                {cfg.donts[i] && <div className="h-full rounded-xl border border-red-100 bg-red-50/60 p-4" data-testid={`dont-item-${i}`}><strong className="text-red-900">{cfg.donts[i].title}</strong><p className="mt-1 text-sm text-red-800/80">{cfg.donts[i].desc}</p></div>}
              </div>
              {i === 0 && <h3 className="md:hidden mt-6 flex items-center gap-2 text-lg font-semibold text-emerald-700 md:order-none"><Check className="h-5 w-5" /> So machst du es richtig</h3>}
              <div className="hidden md:block">
                {cfg.dos[i] && <div className="h-full rounded-xl border border-emerald-100 bg-emerald-50/60 p-4" data-testid={`do-item-${i}`}><strong className="text-emerald-900">{cfg.dos[i].title}</strong><p className="mt-1 text-sm text-emerald-800/80">{cfg.dos[i].desc}</p></div>}
              </div>
            </React.Fragment>
          ))}
          <div className="md:hidden space-y-3">
            {cfg.dos.map((d, i) => <div key={i} className="rounded-xl border border-emerald-100 bg-emerald-50/60 p-4"><strong className="text-emerald-900">{d.title}</strong><p className="mt-1 text-sm text-emerald-800/80">{d.desc}</p></div>)}
          </div>
        </div>
      </SeoSection>

      <SeoSection id="faq" variant="soft" title={cfg.faqTitle} sectionRef={faqRef} testId="faq-section" width="max-w-3xl">
        <div className="space-y-3">
          {cfg.faqs.map((faq, index) => (
            <div key={index} className="overflow-hidden rounded-xl bg-white ring-1 ring-gray-200" data-testid={`faq-item-${index}`}>
              <button type="button" onClick={() => toggleFaq(index, faq.question)} aria-expanded={openFaq === index} className="flex w-full items-center justify-between px-5 py-4 text-left transition-colors hover:bg-gray-50" data-testid={`faq-toggle-${index}`}>
                <span className="pr-4 font-semibold text-gray-900">{faq.question}</span>
                <ChevronDown className={`h-5 w-5 flex-none text-gray-500 transition-transform ${openFaq === index ? 'rotate-180' : ''}`} />
              </button>
              <div className={`px-5 pb-4 text-gray-600 leading-relaxed ${openFaq === index ? '' : 'hidden'}`}>{faq.answer}</div>
            </div>
          ))}
        </div>
      </SeoSection>

      <SeoSection variant="plain" className="!py-10" testId="internal-links-section">
        <div className="grid gap-6 sm:grid-cols-2">
          <div><p className="mb-2 text-sm font-semibold text-gray-500">Weitere Steuerrechner</p><div className="flex flex-wrap gap-x-4 gap-y-1">{cfg.relatedCalculators.map((l, i) => <Link key={i} to={l.to} className="text-emerald-700 underline-offset-4 hover:underline">{l.label}</Link>)}</div></div>
          <div><p className="mb-2 text-sm font-semibold text-gray-500">Auch interessant</p><div className="flex flex-wrap gap-x-4 gap-y-1">{cfg.relatedPages.map((l, i) => <Link key={i} to={l.to} className="text-emerald-700 underline-offset-4 hover:underline">{l.label}</Link>)}</div></div>
        </div>
      </SeoSection>

      <SeoSection variant="accent" sectionRef={finalCtaRef} testId="final-cta-section">
        <div className="max-w-3xl">
          <h2 className="font-display text-3xl sm:text-4xl font-bold">{cfg.finalCtaTitle}</h2>
          <p className="mt-4 text-base md:text-lg text-emerald-100/80">{cfg.finalCtaSubtitle}</p>
          <div className="mt-8 flex flex-col sm:flex-row gap-3">
            <Link to="/signup" onClick={() => trackCTAClick('final_cta', 'Jetzt kostenlos starten')} data-testid="final-cta-button"><Button size="lg" className="w-full bg-white text-[#062417] hover:bg-emerald-50 h-12 px-8 text-base font-semibold">Jetzt kostenlos starten</Button></Link>
            <Button size="lg" variant="outline" onClick={openCalModal} className="border-white/40 bg-transparent text-white hover:bg-white/10 hover:text-white h-12 px-8 text-base" data-testid="final-demo-button"><Video className="h-5 w-5 mr-2" /> Demo buchen</Button>
          </div>
          <div className="mt-8 flex flex-wrap gap-6 text-sm text-emerald-100/80">
            {cfg.finalTrustBadges.map((b, i) => <TrustBadge key={i} {...b} light />)}
            <TrustBadge {...cfg.finalTrustBadgeDynamic(taxYear)} light />
          </div>
        </div>
      </SeoSection>

      <SeoClusterLinks cluster="steuerruecklagen" country={cfg.country} />
      <Footer />
      <StickyMobileCTA onClick={() => trackCTAClick('sticky', 'Kostenlos starten')} />
    </div>
  );
}
